import React, { useState, useEffect } from "react";
import { Routes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "./components/layout/Sidebar";
import Header from "./components/layout/Header";
import Homepage from "./components/pages/Homepage";
import InnovationCenter from "./components/pages/InnovationCenter";
import CompanyResearch from "./components/pages/CompanyResearch";
import WorkflowMap from "./components/pages/WorkflowMap";
import TrizWorkflow from "./components/pages/TrizWorkflow";
import RoadmapGenerator from "./components/pages/RoadmapGenerator";
import RoadmapVisualization from "./components/pages/RoadmapVisualization";
import TimelineView from "./components/pages/TimelineView";
import ArchitectureLayers from "./components/pages/ArchitectureLayers";
import ExportHub from "./components/pages/ExportHub";
import AITrizIdeation from "./components/pages/AITrizIdeation";
import TrizInnovationWorkflow from "./components/pages/TrizInnovationWorkflow";
import RoadmapInnovation from "./components/pages/RoadmapInnovation";
import MBSEOPMFlow from "./components/pages/MBSEOPMFlow";
import PrivateRoute from "./components/PrivateRoute/PrivateRoute";
import Login from "./components/Login/Login"; // <== Make sure this exists

type ProtectedLayoutProps = {
  darkMode: boolean;
  toggleDarkMode: () => void;
  sidebarOpen: boolean;
  toggleSidebar: () => void;
};

const ProtectedLayout = ({
  darkMode,
  toggleDarkMode,
  sidebarOpen,
  toggleSidebar,
}: ProtectedLayoutProps) => {
  return (
    <div className="min-h-screen gradient-bg-subtle transition-colors duration-300">
      <div className="flex h-screen overflow-hidden">
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="flex-shrink-0"
            >
              <Sidebar />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex-1 flex flex-col overflow-hidden">
          <Header
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            sidebarOpen={sidebarOpen}
            toggleSidebar={toggleSidebar}
          />
          <main className="flex-1 overflow-auto bg-gray-50/50 dark:bg-gray-950/50">
            <div className="h-full p-6">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

const AppContent = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/home" element={<Homepage />} />
      <Route path="/innovation-center" element={<InnovationCenter />} />
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Navigate to="/home" replace />} />

      {/* Protected Routes */}
      <Route
        element={
          <PrivateRoute>
            <ProtectedLayout
              darkMode={darkMode}
              toggleDarkMode={() => setDarkMode((d) => !d)}
              sidebarOpen={sidebarOpen}
              toggleSidebar={() => setSidebarOpen((s) => !s)}
            />
          </PrivateRoute>
        }
      >
        <Route path="/ideation" element={<AITrizIdeation />} />
        <Route path="/problem-solving" element={<TrizWorkflow />} />
        <Route path="/roadmap-innovation" element={<RoadmapInnovation />} />
        <Route path="/market-discovery" element={<CompanyResearch />} />
        <Route path="/business-model" element={<TrizInnovationWorkflow />} />
        <Route path="/mbse-opm-flow" element={<MBSEOPMFlow />} />
        <Route
          path="/algorithm-optimization"
          element={<ArchitectureLayers />}
        />
        <Route path="/workflow-map" element={<WorkflowMap />} />
        <Route path="/roadmap-generator" element={<RoadmapGenerator />} />
        <Route
          path="/roadmap-visualization"
          element={<RoadmapVisualization />}
        />
        <Route path="/timeline" element={<TimelineView />} />
        <Route path="/export" element={<ExportHub />} />
      </Route>
    </Routes>
  );
};

export default AppContent;
